type TRgb = [number, number, number];

const parseRgb = (value: string): TRgb | null => {
  const match = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(value);
  if (!match) return null;
  if (match[4] !== undefined && Number(match[4]) !== 1) {
    throw new Error(`Translucent colour has no contrast on its own: ${value}`);
  }
  return [Number(match[1]), Number(match[2]), Number(match[3])];
};

const parseHex = (hex: string): TRgb => {
  const rgb = parseRgb(hex.trim());
  if (rgb) return rgb;
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) throw new Error(`Not a colour: ${hex}`);
  const raw = match[1].length === 3 ? [...match[1]].map(c => c + c).join('') : match[1];
  return [0, 2, 4].map(i => parseInt(raw.slice(i, i + 2), 16)) as TRgb;
};

const toHex = (rgb: TRgb): string =>
  `#${rgb.map(c => Math.round(c).toString(16).padStart(2, '0')).join('').toUpperCase()}`;

const channel = (value: number): number => {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

const luminance = (hex: string): number => {
  const [r, g, b] = parseHex(hex).map(channel);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrastRatio = (a: string, b: string): number => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

export const mixHex = (a: string, b: string, weightOfA: number): string => {
  const ca = parseHex(a);
  const cb = parseHex(b);
  return toHex(ca.map((v, i) => v * weightOfA + cb[i] * (1 - weightOfA)) as TRgb);
};

export const tint = (tone: string, surface: string, amount: number): string =>
  mixHex(tone, surface, amount);
